import { useCallback, useState } from 'react'
import { useLocation } from 'react-router'
import { useCreateConnectAccountLinkMutation } from '@/redux/api/paymentApi'
import { reportFailure } from '@/utils/failureNotice'

/**
 * Start Stripe's hosted onboarding from wherever the vendor is (#1158,
 * Tyler 2026-09-21: the return "should take them back to whatever screen
 * they were on when they left it").
 *
 * The page the vendor is on rides to the backend as `returnTo`; Stripe's
 * return and refresh URLs carry it, and StripeOnboardingReturn lands the
 * vendor back on it when they finish or leave. One hook for every Connect
 * button (the setup banner, the Stripe Connect page) so no page builds
 * its own redirect or forgets the return path.
 *
 * `start` is a full-page redirect: Stripe's onboarding is not ours to
 * embed, and the link is single-use, so nothing is kept here but the
 * loading flag for the button.
 */
export function useStripeOnboarding() {
  const location = useLocation()
  const [createConnectAccountLink] = useCreateConnectAccountLinkMutation()
  const [loading, setLoading] = useState(false)

  const start = useCallback(async () => {
    if (loading) return
    setLoading(true)
    try {
      // unwrap() resolves with the bare onboarding URL (the slice unwraps
      // and schema-validates the envelope); failures reject into the catch.
      const onboardingUrl = await createConnectAccountLink({
        returnTo: `${location.pathname}${location.search}`,
      }).unwrap()
      window.location.href = onboardingUrl
    } catch (err) {
      reportFailure(err)
    } finally {
      setLoading(false)
    }
  }, [loading, createConnectAccountLink, location.pathname, location.search])

  return { start, loading }
}
